/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from "@emotion/react";
import { Table as BootstrapTable } from "reactstrap";
import { PAGE_LIMIT_ITEMS } from "../../hooks";
import { Loading } from "./Loading";
import { PaginationRow } from "./PaginationRow";

export const stickyHeader = css`
    right: 0;
    padding-left: 16px;
    z-index: 1;

    &:after {
        content: "";
        position: absolute;
        width: 1px;
        height: 100%;
        left: -1px;
        bottom: 0;
        top: 0;
        background-color: white!important;
    }
`;

export const TableHeaderCell = ({ children, className, styles = null, sticky }: { children?: React.ReactNode, className?: string, styles?: any, sticky?: boolean }) => {
    return (
        <th
            className={`align-middle text-start text-nowrap fw-bold py-1 px-3 ${className} ${sticky ? "position-sticky px-0 py-0" : ""}`}
            css={[css`background: #e5e8e5!important; max-width: 200px; &:not(:last-child) { border-right: 1px solid white; }`, styles, sticky && stickyHeader]}
        >

            {children}
        </th>
    );
};

const stickyTableCell = css`
    right: 0;
    z-index: 1;
    padding: 0 !important;

    &:after {
        content: "";
        position: absolute;
        width: 100%; height: 4px;
        left: 0;
        right: 0;
        bottom: -3px;
        background-color: white!important;
    }

    &:before {
        content: "";
        position: absolute;
        width: 100%;
        height: 4px;
        left: 0;
        right: 0;
        top: -3px;
        background-color: white!important;
    }
`;

type TableCellProps = React.HTMLAttributes<HTMLTableCellElement> & {
    children?: React.ReactNode;
    className?: string;
    styles?: SerializedStyles | null;
    colSpan?: number;
    sticky?: boolean;
    width?: string;
};

const TableCellStickyWrapper = ({ children }) => (
    <div className="d-flex align-items-center gap-2 py-2 px-2 px-sm-3" css={css`background-color: white!important; box-shadow: -3px 0 5px 0 #e5e8e5;);`}>
        {children}
    </div>
);

export const TableCell = ({
    children,
    className = "text-start align-middle text-nowrap px-3",
    styles = null,
    sticky,
    width = "unset",
    ...props
}: TableCellProps) => {
    const ContentWrapper = sticky ? TableCellStickyWrapper : "div";

    return (
        <td className={`text-start align-middle text-nowrap px-3 ${className} ${sticky ? "position-sticky px-0 py-0" : ""}`} css={[css`width: ${width}; &:has(+ td.position-sticky) div { padding-right: 24px!important; }`, styles, sticky && stickyTableCell]} {...props}>
            <ContentWrapper className="text-nowrap">
                {children}
            </ContentWrapper>
        </td>
    );
};

export const TableWrapper = ({ children }) => {
    return <div css={css`border-radius: 8px; overflow: hidden;`} className="bg-body border">{children}</div>;
};

export const getTableHead = (columns) => columns.map((field, index) => (
    <TableHeaderCell
        key={index}
        className={`${"lg" in field ? `col-${field.lg}` : ""}`}
    >
        {field.label}
    </TableHeaderCell>
));

export const getRowValues = (p, columns) => columns.map((i, index) => (
    <TableCell key={index}>
        {i.display_component ? i.display_component(p) : p[i.name]}
    </TableCell>
));

type TableProps = {
    isLoading?: boolean;
    withWrapper?: boolean;
    count: number;
    customLimit?: number;
    emptyStateText?: string;
    emptyStateActions?: React.ReactNode;
    header: React.ReactNode;
    children: React.ReactNode;
    minHeight?: string;
};

export const Table = ({
    header,
    isLoading,
    withWrapper,
    count,
    customLimit,
    emptyStateText,
    emptyStateActions,
    children,
    minHeight = "50vh",
}: TableProps) => {
    const Wrapper = withWrapper ? TableWrapper : "div";
    const pageLimit = customLimit ? Number(customLimit) : PAGE_LIMIT_ITEMS[0];

    return (
        <div >
            <div css={css`min-height: ${minHeight};`}>
                {isLoading
                    ? <Loading />
                    : (
                        <Wrapper>
                            <BootstrapTable size="sm" responsive={true} borderless={true} css={css`margin-bottom: 4px;`}>
                                <thead className="w-100" css={css`z-index: 10; position: relative;`}>
                                    {header}
                                </thead>

                                {children}
                            </BootstrapTable>

                            {count === 0 && !isLoading && (
                                <div className="text-center pb-3">
                                    <div className="text-center d-flex align-items-center justify-content-center" css={css`min-height: ${minHeight};`}>
                                        <div className="d-flex flex-column align-items-center justify-content-center">
                                            <h6 className="text-muted">
                                                {emptyStateText ?? "No results"}
                                            </h6>

                                            {emptyStateActions}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Wrapper>
                    )}
            </div>

            {!!count && count > pageLimit && (
                <PaginationRow totalCount={count} customLimit={customLimit} />
            )}
        </div>
    );
};
